import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, RefreshCw, ExternalLink, Clock, Briefcase, FileText, Mic as Mic2, Presentation, MessageCircle, Lightbulb, CircleCheck as CheckCircle2, TriangleAlert as AlertTriangle, Flag, CircleAlert as AlertCircle, User, Sparkles } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { sanitizeUrl } from '../../../lib/sanitizeUrl';
import { Sentry } from '../../../lib/sentry';
import { useApi } from '../../../lib/useApi';
import { logAccountActivity } from '../../../lib/activityLog';
import { useRealtimeStatus } from '../../../lib/useRealtimeStatus';
import { useLlmJobsPoll } from '../../../lib/useLlmJobsPoll';
import type {
  Account, Contact, ContactActivityResearch, HallucinationReport,
  PublishedContentItem, PodcastAppearanceItem, SpeakingEngagementItem,
  PublicOpinionItem, CareerHighlightItem,
} from '../../../types';
import LoadingSpinner from '../../common/LoadingSpinner';

interface Props {
  account: Account;
  contact: Contact;
}

const CONFIDENCE_CONFIG: Record<string, { label: string; bg: string; text: string; icon: typeof CheckCircle2 }> = {
  verified: { label: 'Identity Verified', bg: 'bg-app-positive/10', text: 'text-app-positive', icon: CheckCircle2 },
  likely: { label: 'Likely Match', bg: 'bg-app-warning/10', text: 'text-app-warning', icon: AlertTriangle },
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function daysSince(dateStr: string | null): number {
  if (!dateStr) return Infinity;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
}

function StalenessIndicator({ researched_at }: { researched_at: string | null }) {
  const days = daysSince(researched_at);
  let color = 'text-app-positive';
  let label = 'Fresh';
  if (days > 30) { color = 'text-app-critical'; label = 'Stale'; }
  else if (days > 14) { color = 'text-app-warning'; label = 'Ageing'; }

  return (
    <div className="flex items-center gap-1.5 text-xs">
      <Clock className={`w-3.5 h-3.5 ${color}`} />
      <span className="text-app-muted">Researched {formatDate(researched_at)}</span>
      <span className={`font-medium ${color}`}>({label})</span>
    </div>
  );
}

function ReportButton({
  researchId,
  fieldPath,
  reports,
  userId,
  onReport,
}: {
  researchId: string;
  fieldPath: string;
  reports: HallucinationReport[];
  userId: string;
  onReport: (fieldPath: string) => void;
}) {
  const alreadyReported = reports.some(
    (r) => r.field_path === fieldPath && r.reported_by === userId
  );
  const totalReports = reports.filter((r) => r.field_path === fieldPath).length;

  if (alreadyReported) return null;

  return (
    <button
      onClick={() => onReport(fieldPath)}
      className="flex items-center gap-1 text-[10px] text-app-muted hover:text-app-critical transition-colors"
      title="Report as hallucination"
    >
      <Flag className="w-3 h-3" />
      {totalReports > 0 && <span>{totalReports} reported</span>}
    </button>
  );
}

function isReported(reports: HallucinationReport[], fieldPath: string): boolean {
  return reports.some((r) => r.field_path === fieldPath);
}

function PublishedContentCard({
  item, index, researchId, reports, userId, onReport,
}: {
  item: PublishedContentItem; index: number; researchId: string;
  reports: HallucinationReport[]; userId: string; onReport: (fp: string) => void;
}) {
  const fp = `published_content.${index}`;
  const reported = isReported(reports, fp);

  return (
    <div className={`p-3 bg-app-surface rounded-lg border border-app-line ${reported ? 'opacity-50' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <a href={sanitizeUrl(item.url)} target="_blank" rel="noopener noreferrer"
            className="text-sm font-medium text-forest-700 hover:text-forest-800 flex items-center gap-1.5">
            <span className="truncate">{item.title}</span>
            <ExternalLink className="w-3 h-3 flex-shrink-0" />
          </a>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-app-muted">{item.platform}</span>
            {item.date && <span className="text-[10px] text-app-muted">{item.date}</span>}
          </div>
        </div>
        {!reported && (
          <ReportButton researchId={researchId} fieldPath={fp}
            reports={reports} userId={userId} onReport={onReport} />
        )}
      </div>
      {item.summary && <p className="text-xs text-app-muted mt-1.5 leading-relaxed">{item.summary}</p>}
      {item.sales_angle && (
        <div className="mt-1.5 flex items-start gap-1.5">
          <Sparkles className="w-3 h-3 text-app-warning flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-app-warning leading-relaxed">{item.sales_angle}</p>
        </div>
      )}
      {reported && <p className="text-[10px] text-app-critical mt-1">Reported as potentially inaccurate</p>}
    </div>
  );
}

function PodcastCard({
  item, index, researchId, reports, userId, onReport,
}: {
  item: PodcastAppearanceItem; index: number; researchId: string;
  reports: HallucinationReport[]; userId: string; onReport: (fp: string) => void;
}) {
  const fp = `podcast_appearances.${index}`;
  const reported = isReported(reports, fp);

  return (
    <div className={`p-3 bg-app-surface rounded-lg border border-app-line ${reported ? 'opacity-50' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <a href={sanitizeUrl(item.url)} target="_blank" rel="noopener noreferrer"
            className="text-sm font-medium text-forest-700 hover:text-forest-800 flex items-center gap-1.5">
            <span className="truncate">{item.episode_title || item.podcast_name}</span>
            <ExternalLink className="w-3 h-3 flex-shrink-0" />
          </a>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-app-muted">{item.podcast_name}</span>
            {item.date && <span className="text-[10px] text-app-muted">{item.date}</span>}
          </div>
        </div>
        {!reported && (
          <ReportButton researchId={researchId} fieldPath={fp}
            reports={reports} userId={userId} onReport={onReport} />
        )}
      </div>
      {item.key_topics?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {item.key_topics.map((t, i) => (
            <span key={i} className="px-1.5 py-0.5 text-[9px] font-medium bg-app-canvas text-app-muted rounded">{t}</span>
          ))}
        </div>
      )}
      {item.sales_angle && (
        <div className="mt-1.5 flex items-start gap-1.5">
          <Sparkles className="w-3 h-3 text-app-warning flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-app-warning leading-relaxed">{item.sales_angle}</p>
        </div>
      )}
      {reported && <p className="text-[10px] text-app-critical mt-1">Reported as potentially inaccurate</p>}
    </div>
  );
}

function SpeakingCard({
  item, index, researchId, reports, userId, onReport,
}: {
  item: SpeakingEngagementItem; index: number; researchId: string;
  reports: HallucinationReport[]; userId: string; onReport: (fp: string) => void;
}) {
  const fp = `speaking_engagements.${index}`;
  const reported = isReported(reports, fp);

  return (
    <div className={`p-3 bg-app-surface rounded-lg border border-app-line ${reported ? 'opacity-50' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <a href={sanitizeUrl(item.url)} target="_blank" rel="noopener noreferrer"
            className="text-sm font-medium text-forest-700 hover:text-forest-800 flex items-center gap-1.5">
            <span className="truncate">{item.topic}</span>
            <ExternalLink className="w-3 h-3 flex-shrink-0" />
          </a>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-app-muted">{item.event}</span>
            {item.date && <span className="text-[10px] text-app-muted">{item.date}</span>}
          </div>
        </div>
        {!reported && (
          <ReportButton researchId={researchId} fieldPath={fp}
            reports={reports} userId={userId} onReport={onReport} />
        )}
      </div>
      {item.sales_angle && (
        <div className="mt-1.5 flex items-start gap-1.5">
          <Sparkles className="w-3 h-3 text-app-warning flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-app-warning leading-relaxed">{item.sales_angle}</p>
        </div>
      )}
      {reported && <p className="text-[10px] text-app-critical mt-1">Reported as potentially inaccurate</p>}
    </div>
  );
}

function OpinionCard({
  item, index, researchId, reports, userId, onReport,
}: {
  item: PublicOpinionItem; index: number; researchId: string;
  reports: HallucinationReport[]; userId: string; onReport: (fp: string) => void;
}) {
  const fp = `public_opinions.${index}`;
  const reported = isReported(reports, fp);

  return (
    <div className={`p-3 bg-app-surface rounded-lg border border-app-line ${reported ? 'opacity-50' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-app-ink">{item.topic}</p>
          {item.quote && (
            <blockquote className="mt-1 text-xs text-app-muted italic border-l-2 border-app-line pl-2">
              "{item.quote}"
            </blockquote>
          )}
          <p className="text-xs text-app-muted mt-1">{item.stance}</p>
        </div>
        {!reported && (
          <ReportButton researchId={researchId} fieldPath={fp}
            reports={reports} userId={userId} onReport={onReport} />
        )}
      </div>
      {item.source_url && (
        <a href={sanitizeUrl(item.source_url)} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[10px] text-forest-600 hover:text-forest-700 mt-1.5">
          <ExternalLink className="w-2.5 h-2.5" /> Source
        </a>
      )}
      {item.sales_angle && (
        <div className="mt-1.5 flex items-start gap-1.5">
          <Sparkles className="w-3 h-3 text-app-warning flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-app-warning leading-relaxed">{item.sales_angle}</p>
        </div>
      )}
      {reported && <p className="text-[10px] text-app-critical mt-1">Reported as potentially inaccurate</p>}
    </div>
  );
}

export default function PersonaActivitySection({ account, contact }: Props) {
  const { apiGet, apiPost } = useApi();
  const { user, organisation } = useAuth();
  const [research, setResearch] = useState<ContactActivityResearch | null>(null);
  const [reports, setReports] = useState<HallucinationReport[]>([]);
  const userId = user?.id ?? '';
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const processing = (contact.activity_research_status ?? 'none') === 'processing';
  const showLoading = launching || processing;

  // Contact activity research now runs as a background job; drive the generic poll while this
  // contact is processing so the run is retrieved and finalized (the useRealtimeStatus above
  // observes activity_research_status flipping terminal).
  useLlmJobsPoll(organisation?.id, showLoading);

  const loadResearch = useCallback(async () => {
    try {
      const { research: row, reports: hallReports } = await apiGet<{
        research: ContactActivityResearch | null;
        reports: HallucinationReport[];
        activity_research_status: string | null;
      }>(`/v1/contacts/${contact.id}/activity-research`);
      setResearch(row);
      setReports(hallReports ?? []);
    } catch (err) {
      Sentry.captureException(err, { tags: { context: 'contact-activity-research-load' } });
    }
    setLoaded(true);
  }, [apiGet, contact.id]);

  const loadResearchRef = useRef(loadResearch);
  loadResearchRef.current = loadResearch;

  useEffect(() => { loadResearch(); }, [loadResearch]);

  useRealtimeStatus({
    table: 'contacts',
    filterColumn: 'id',
    filterValue: contact.id,
    enabled: processing || launching,
    statusField: 'activity_research_status',
    terminalStatuses: ['completed', 'error'],
    fallbackIntervalMs: 3000,
    fetcher: useCallback(async () => {
      const { activity_research_status } = await apiGet<{ activity_research_status: string | null }>(
        `/v1/contacts/${contact.id}/activity-research`,
      );
      return { activity_research_status } as Record<string, unknown>;
    }, [apiGet, contact.id]),
    onUpdate: useCallback((row: Record<string, unknown>) => {
      const status = row.activity_research_status as string;
      if (status !== 'processing') {
        setLaunching(false);
        if (status === 'completed') {
          loadResearchRef.current();
        }
        if (status === 'error') {
          setError('Persona activity research failed. Please try again.');
        }
      }
    }, []),
  });

  const runResearch = async () => {
    setLaunching(true);
    setError('');
    try {
      apiPost('/v1/research-contact-activity', { contact_id: contact.id, account_id: account.id })
        .catch((err: unknown) => { Sentry.captureException(err, { tags: { context: 'research-contact-activity' } }); });
      logAccountActivity(account.id, 'contact_activity_research_started',
        `Started activity research for ${contact.first_name} ${contact.last_name}`);
    } catch (err) {
      Sentry.captureException(err, { tags: { context: 'contact_activity_research' } });
      setError(err instanceof Error ? err.message : 'Failed to start research');
      setLaunching(false);
    }
  };

  const reportHallucination = async (fieldPath: string) => {
    if (!research || !userId) return;
    try {
      const { report } = await apiPost<{ report: HallucinationReport }>(
        `/v1/contacts/${contact.id}/hallucination-reports`,
        { field_path: fieldPath },
      );
      setReports((prev) => [...prev, report]);
    } catch (err) {
      Sentry.captureException(err, { tags: { context: 'contact-activity-hallucination-report' } });
    }
  };

  const contactName = `${contact.first_name} ${contact.last_name}`.trim();
  const activityStatus = contact.activity_research_status ?? 'none';
  const completed = activityStatus === 'completed' || research?.status === 'completed';
  const hasError = activityStatus === 'error';
  const hasContent = research && (
    research.published_content.length > 0 ||
    research.podcast_appearances.length > 0 ||
    research.speaking_engagements.length > 0 ||
    research.public_opinions.length > 0 ||
    research.career_highlights.length > 0
  );

  if (!loaded) {
    return (
      <div className="flex items-center justify-center py-6">
        <LoadingSpinner size="sm" />
      </div>
    );
  }

  const confidence = research
    ? CONFIDENCE_CONFIG[research.disambiguation_confidence] || CONFIDENCE_CONFIG.likely
    : null;
  const ConfIcon = confidence?.icon || CheckCircle2;

  return (
    <div className="rounded-lg border border-app-line bg-app-surface shadow-none overflow-hidden">
      <div className="bg-forest-600 px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-white" />
          <h4 className="text-sm font-semibold text-white">Persona Activity</h4>
          {research && confidence && (
            <span className={`ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium ${confidence.bg} ${confidence.text}`}>
              <ConfIcon className="w-2.5 h-2.5" />
              {confidence.label}
            </span>
          )}
        </div>
        <button
          onClick={runResearch}
          disabled={showLoading}
          className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-white/90 hover:text-white hover:bg-app-surface/10 rounded transition-colors disabled:opacity-40"
        >
          {showLoading ? (
            <><LoadingSpinner size="sm" /> Researching...</>
          ) : (
            <><RefreshCw className="w-3.5 h-3.5" /> {research ? 'Re-research' : 'Research'}</>
          )}
        </button>
      </div>

      <div className="p-5">
        {showLoading && (
          <div className="flex items-center gap-3 p-4 bg-forest-50 border border-forest-100 rounded-lg">
            <Search className="w-5 h-5 text-forest-600 animate-pulse" />
            <div>
              <p className="text-sm font-medium text-forest-800">Researching {contactName}</p>
              <p className="text-xs text-forest-600">Searching the web for blog posts, podcast appearances, speaking engagements, and public activity. This typically takes 20-40 seconds.</p>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 p-3 bg-app-critical/10 border border-app-critical rounded-lg">
            <AlertCircle className="w-4 h-4 text-app-critical flex-shrink-0" />
            <p className="text-xs text-app-critical">{error}</p>
          </div>
        )}

        {!showLoading && !research && !hasError && (
          <div className="text-center py-4">
            <Search className="w-8 h-8 text-app-line-strong mx-auto mb-2" />
            <p className="text-sm text-app-muted">
              Research {contactName}'s public activity to find conversation starters,
              shared interests, and actionable intelligence.
            </p>
            <button onClick={runResearch} disabled={showLoading} className="mt-3 btn-primary !py-2 !px-4 !text-sm">
              <Search className="w-4 h-4 mr-1.5" /> Research This Person
            </button>
          </div>
        )}

        {!showLoading && hasError && !error && (
          <div className="text-center py-4">
            <AlertCircle className="w-8 h-8 text-app-critical mx-auto mb-2" />
            <p className="text-sm text-app-critical mb-3">Research failed. Please try again.</p>
            <button onClick={runResearch} disabled={showLoading} className="btn-primary !py-2 !px-4 !text-sm">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Retry
            </button>
          </div>
        )}

        {!showLoading && research && completed && (
          <div className="space-y-4">
            {research.disambiguation_confidence === 'likely' && (
              <div className="flex items-center gap-1.5 px-3 py-2 bg-app-warning/10 border border-app-warning rounded-lg">
                <AlertTriangle className="w-3.5 h-3.5 text-app-warning flex-shrink-0" />
                <span className="text-[11px] text-app-warning">
                  The identity match is probable but not confirmed. The findings may need manual verification.
                </span>
              </div>
            )}

            {research.sales_intelligence && (
              <div className="bg-app-warning/10 border border-app-warning rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-app-warning" />
                  <h5 className="text-xs font-bold text-app-warning uppercase tracking-wider">Sales Intelligence</h5>
                </div>
                <ul className="space-y-1.5">
                  {research.sales_intelligence
                    .split('\n')
                    .map(line => line.replace(/^[\s\-\*\u2022\u25CF\u25CB]+/, '').trim())
                    .filter(line => line.length > 0)
                    .map((bullet, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-app-warning flex-shrink-0" />
                        <span className="text-sm text-app-warning leading-relaxed">{bullet}</span>
                      </li>
                    ))}
                </ul>
              </div>
            )}

            {research.research_summary && (
              <p className="text-sm text-app-muted leading-relaxed">{research.research_summary}</p>
            )}

            {research.interests_and_themes.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {research.interests_and_themes.map((theme, i) => (
                  <span key={i} className="px-2 py-1 text-[11px] font-medium bg-forest-50 text-forest-700 border border-forest-100 rounded-full">
                    {theme}
                  </span>
                ))}
              </div>
            )}

            {hasContent && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-xs text-forest-600 hover:text-forest-700 font-medium transition-colors"
              >
                {expanded ? 'Hide detailed findings' : 'Show detailed findings'}
              </button>
            )}

            {expanded && hasContent && (
              <div className="space-y-5 pt-2">
                {research.published_content.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-app-focus" />
                      <h5 className="text-xs font-bold text-app-ink uppercase tracking-wider">Published Content</h5>
                      <span className="text-[10px] text-app-muted">{research.published_content.length}</span>
                    </div>
                    <div className="space-y-2">
                      {research.published_content.map((item, i) => (
                        <PublishedContentCard key={i} item={item} index={i}
                          researchId={research.id} reports={reports}
                          userId={userId} onReport={reportHallucination} />
                      ))}
                    </div>
                  </div>
                )}

                {research.podcast_appearances.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Mic2 className="w-4 h-4 text-app-focus" />
                      <h5 className="text-xs font-bold text-app-ink uppercase tracking-wider">Podcast Appearances</h5>
                      <span className="text-[10px] text-app-muted">{research.podcast_appearances.length}</span>
                    </div>
                    <div className="space-y-2">
                      {research.podcast_appearances.map((item, i) => (
                        <PodcastCard key={i} item={item} index={i}
                          researchId={research.id} reports={reports}
                          userId={userId} onReport={reportHallucination} />
                      ))}
                    </div>
                  </div>
                )}

                {research.speaking_engagements.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Presentation className="w-4 h-4 text-app-focus" />
                      <h5 className="text-xs font-bold text-app-ink uppercase tracking-wider">Speaking Engagements</h5>
                      <span className="text-[10px] text-app-muted">{research.speaking_engagements.length}</span>
                    </div>
                    <div className="space-y-2">
                      {research.speaking_engagements.map((item, i) => (
                        <SpeakingCard key={i} item={item} index={i}
                          researchId={research.id} reports={reports}
                          userId={userId} onReport={reportHallucination} />
                      ))}
                    </div>
                  </div>
                )}

                {research.public_opinions.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <MessageCircle className="w-4 h-4 text-app-critical" />
                      <h5 className="text-xs font-bold text-app-ink uppercase tracking-wider">Public Opinions</h5>
                      <span className="text-[10px] text-app-muted">{research.public_opinions.length}</span>
                    </div>
                    <div className="space-y-2">
                      {research.public_opinions.map((item, i) => (
                        <OpinionCard key={i} item={item} index={i}
                          researchId={research.id} reports={reports}
                          userId={userId} onReport={reportHallucination} />
                      ))}
                    </div>
                  </div>
                )}

                {research.career_highlights.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Briefcase className="w-4 h-4 text-app-muted" />
                      <h5 className="text-xs font-bold text-app-ink uppercase tracking-wider">Career Highlights</h5>
                    </div>
                    <div className="space-y-2">
                      {research.career_highlights.map((item: CareerHighlightItem, i: number) => (
                        <div key={i} className="flex items-start gap-3 p-2.5 bg-app-surface rounded-lg border border-app-line">
                          <div className="w-1.5 h-1.5 rounded-full bg-app-muted mt-1.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-app-ink">{item.role}</p>
                            <p className="text-xs text-app-muted">{item.company} {item.timeframe && `(${item.timeframe})`}</p>
                            {item.notable && <p className="text-xs text-app-muted mt-0.5">{item.notable}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {research.social_activity_summary && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Lightbulb className="w-4 h-4 text-app-warning" />
                      <h5 className="text-xs font-bold text-app-ink uppercase tracking-wider">Social Activity</h5>
                    </div>
                    <p className="text-xs text-app-muted leading-relaxed">{research.social_activity_summary}</p>
                  </div>
                )}

                {research.disambiguation_verification && (
                  <div className="mt-2 px-3 py-2 bg-app-canvas border border-app-line rounded-lg">
                    <p className="text-[10px] font-semibold text-app-muted uppercase tracking-wider mb-0.5">Identity Verification</p>
                    <p className="text-xs text-app-muted">{research.disambiguation_verification}</p>
                  </div>
                )}
              </div>
            )}

            {!hasContent && completed && (
              <div className="text-center py-2">
                <p className="text-sm text-app-muted">Limited public activity found for {contactName}.</p>
              </div>
            )}

            <div className="pt-2 border-t border-app-line">
              <StalenessIndicator researched_at={research.researched_at} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
